import type { EmployeeSummary, Item, WorkKind, WorkspaceSummary } from '../../domain/models';
import { can, needsDecision } from '../../domain/models';

// The inbox's shape, as in the mobile app (work_groups.dart): one block per employee, and a burst of
// similar work folded into one line ("6 žinutės") instead of six cards.

/** A line in a block: one item, or several of a kind folded into one. */
export type WorkEntry =
  | { type: 'single'; item: Item }
  | {
      type: 'folded';
      /** Stable across refreshes, so an open fold stays open. */
      key: string;
      kind: WorkKind;
      /** The network for posts, so Facebook and Reddit posts fold apart. */
      channel?: string;
      items: Item[];
    };

/** One employee's waiting work. */
export interface EmployeeWork {
  employee: EmployeeSummary;
  workspace: WorkspaceSummary;
  /** In the order they came from the server: most urgent first, then oldest first. */
  items: Item[];
  entries: WorkEntry[];
}

/** Up to this many items stay one per line; more get folded by kind. */
export const foldAbove = 3;

/** Approved work the person carries out and then reports done; failed work counts as failed. */
const toPublish = (i: Item) => i.status === 'approved' && can(i, 'report_result');
const canDecide = (i: Item) => can(i, 'approve') || can(i, 'reject') || can(i, 'request_changes');

export const isUrgent = (w: EmployeeWork) =>
  w.items.some((i) => needsDecision(i) && (i.priority === 'high' || i.type === 'screening'));
/** What the person can do now, as each item's actions say: a viewer has nothing to do. */
export const toDecideCount = (items: Item[]) => items.filter((i) => canDecide(i) && i.type !== 'report').length;
export const toPublishCount = (items: Item[]) => items.filter(toPublish).length;
export const failedCount = (items: Item[]) => items.filter((i) => i.status === 'failed').length;
/** Pending items the employee revised after the person asked for changes. */
export const revisedCount = (items: Item[]) => items.filter((i) => needsDecision(i) && i.revision > 1).length;
/** Reports the person can mark seen. */
export const reportsToRead = (items: Item[]) => items.filter((i) => i.type === 'report' && can(i, 'approve'));
export const latest = (items: Item[]) => new Date(Math.max(...items.map((i) => i.updatedAt.getTime())));

/** One block per employee, in the order of each one's first item, so the most urgent work comes first. */
export function groupByEmployee(items: Item[]): EmployeeWork[] {
  const byEmployee = new Map<string, Item[]>();
  for (const i of items) {
    const list = byEmployee.get(i.employee.id);
    if (list) list.push(i);
    else byEmployee.set(i.employee.id, [i]);
  }
  return [...byEmployee.values()].map((list) => ({
    employee: list[0]!.employee,
    workspace: list[0]!.workspace,
    items: list,
    entries: foldByKind(list),
  }));
}

/** Every item in the order the list shows them: block by block, line by line, a fold's items in its place. */
export const displayOrder = (groups: EmployeeWork[]): Item[] =>
  groups.flatMap((g) => g.entries.flatMap((e) => (e.type === 'single' ? [e.item] : e.items)));

/** The fold that holds an item, if it is folded. */
export function foldKeyOf(groups: EmployeeWork[], id: string): string | undefined {
  for (const g of groups) {
    for (const e of g.entries) if (e.type === 'folded' && e.items.some((i) => i.id === id)) return e.key;
  }
  return undefined;
}

/** What to open once the person is done with `done`: the next line after it in `order` (the list as it
 * was) that is still waiting, else the nearest one before it, else anything still waiting. */
export function nextAfter(order: readonly string[], done: string, waiting: readonly string[]): string | undefined {
  const left = new Set(waiting.filter((id) => id !== done));
  const at = order.indexOf(done);
  if (at >= 0) {
    for (let i = at + 1; i < order.length; i++) if (left.has(order[i]!)) return order[i];
    for (let i = at - 1; i >= 0; i--) if (left.has(order[i]!)) return order[i];
  }
  return waiting.find((id) => id !== done);
}

const postChannel = (i: Item) =>(i.kind === 'post' && i.payload.type === 'social_post' ? i.payload.channel : undefined);
const foldKey = (i: Item) => {
  const channel = postChannel(i);
  return channel ? `post:${channel}` : i.kind;
};

/** Few items stay one per line. More get folded by kind, in the order each kind first appears; a kind
 * with a single item stays a line of its own, as folding it would only hide it. */
export function foldByKind(items: Item[], above = foldAbove): WorkEntry[] {
  if (items.length <= above) return items.map((item) => ({ type: 'single', item }));
  const byKey = new Map<string, Item[]>();
  for (const i of items) {
    const key = foldKey(i);
    const list = byKey.get(key);
    if (list) list.push(i);
    else byKey.set(key, [i]);
  }
  return [...byKey.entries()].map(([key, list]): WorkEntry => {
    const first = list[0]!;
    if (list.length === 1) return { type: 'single', item: first };
    return { type: 'folded', key: `${first.employee.id}/${key}`, kind: first.kind, channel: postChannel(first), items: list };
  });
}
